import moment from 'moment';
import React, { useState, ReactElement, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { TIME_INPUT_TYPE } from '../constants';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_LABEL,
  DEFAULT_DATE_VALUE,
  EMPTY_ERROR,
  FORM_MODE,
} from './utils';
import ClearIcon from 'src/assets/hosLogs/clear-icon.svg';
import SimpleSelect from '../../../components/Dropdown/SimpleSelect';
import { EDGE_HOURS } from '../../../components/LogView/utils';
import { formatTimeAsMoment } from '../../../utils/dateUtils';

type props = {
  timezoneText: string;
  label: string;
  setTimeValue: (t: moment.Moment) => void;
  dayStartHour?: number;
  isDisabled: boolean;
  value: string;
  formMode: number;
  timeType: string;
  isRequired: boolean;
  setTimeInputError: (v: boolean) => void;
  setDateInputError?: (v: boolean) => void;
  startDateLog?: any;
  endDateLog?: any;
  is36HoursMode?: boolean;
  setDateValue?: (d: any) => void;
};

type DateOption = {
  value: string;
  label: string;
};

const INVALID_FORMAT_ERROR = 'Error, time entered is invalid.';
const REQUIRED_ERROR = 'Error: Field is required';
const INCOMPLETE_ERROR = 'Error: Time entered is incomplete';
const DEFAULT_TIME = '--';
const TIME_TYPE = {
  HH: 'hour',
  MM: 'minute',
};

const TimeInput = ({
  timezoneText,
  label,
  setTimeValue,
  dayStartHour,
  isDisabled,
  value,
  formMode,
  timeType,
  isRequired,
  setTimeInputError,
  setDateInputError = () => null,
  is36HoursMode = false,
  startDateLog,
  endDateLog,
  setDateValue,
}: props): ReactElement | null => {
  const [error, setError] = useState('');
  const [dateError, setDateError] = useState('');
  const [hh, setHH] = useState(DEFAULT_TIME);
  const [mm, setMM] = useState(DEFAULT_TIME);
  const [date, setDate] = useState(DEFAULT_DATE_VALUE);

  const [hhErrorPresent, setHHErrorPresent] = useState(false);
  const [mmErrorPresent, setMMErrorPresent] = useState(false);

  const [shouldRunValidation, setShouldRunValidation] = useState(false);

  const ref = useRef<any>(null);

  useEffect(() => {
    validateDateTimeInput(hh, mm, date, is36HoursMode);
  }, [hh, mm, date]);

  const validateDateTimeInput = (hh: string, mm: string, date: string, is36HoursMode: boolean) => {
    switch (true) {
      case isRequired && is36HoursMode: {
        if (date === DEFAULT_DATE_VALUE || (hh === DEFAULT_TIME && mm === DEFAULT_TIME)) {
          setDateError(REQUIRED_ERROR);
          setDateInputError(true);
        } else {
          setDateError(EMPTY_ERROR);
          setDateInputError(false);
        }
        break;
      }
      case isRequired && !is36HoursMode: {
        if (hh === DEFAULT_TIME && mm === DEFAULT_TIME) {
          setDateError(REQUIRED_ERROR);
          setDateInputError(true);
        } else {
          setDateError(EMPTY_ERROR);
          setDateInputError(false);
        }
        break;
      }
      case !isRequired && is36HoursMode: {
        const validCondition =
          (date && hh !== DEFAULT_TIME && mm !== DEFAULT_TIME) ||
          (!date && hh === DEFAULT_TIME && mm === DEFAULT_TIME);
        if (!validCondition) {
          setDateError(INCOMPLETE_ERROR);
          setDateInputError(true);
        } else {
          setDateError(EMPTY_ERROR);
          setDateInputError(false);
        }
        break;
      }
      default: {
        setDateError(EMPTY_ERROR);
        setDateInputError(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      e.stopPropagation();
      if (ref.current && ref.current?.contains(e.target)) {
        ref && setShouldRunValidation(false);
      } else {
        setShouldRunValidation(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [ref]);

  function formatTimePart(val: string): string {
    if (!val?.length || isNaN(Number(val))) {
      return val;
    }
    return val.replace('-', '');
  }

  useEffect(() => {
    if (formMode !== FORM_MODE.ADD || timeType !== TIME_INPUT_TYPE.END_TIME) {
      const formatted = convertDateToHHMMFormat(value, timezoneText);
      if (formatted) {
        const [hh, mm] = formatted.time.split(':');
        setHH(hh);
        setMM(mm);
        setDate(formatted.date);
      }
    }
    if (formMode === FORM_MODE.ADD && timeType === TIME_INPUT_TYPE.END_TIME) {
      const formatted = convertDateToHHMMFormat(value, timezoneText);
      setDate(formatted.date);
    }
  }, [value, timezoneText]);

  function convertAndSetDateValue(timeValue: string): void {
    let dateOfTime = startDateLog;
    if (
      !is36HoursMode &&
      dayStartHour === EDGE_HOURS.NOON.value &&
      formatTimeAsMoment(timeValue).isBefore(formatTimeAsMoment('12:00'), 'minutes')
    ) {
      dateOfTime = endDateLog;
    }
    if (is36HoursMode) {
      dateOfTime = date;
    }
    setDate(dateOfTime);
    const temp = moment.tz(`${dateOfTime} ${timeValue}`, timezoneText);
    const finalTime = temp.toISOString();
    setTimeValue(moment(finalTime));
  }

  function handleHighlightContent(e: any) {
    e.target.select();
  }

  function handleOnChange(e: any, timeType: string): void {
    e.preventDefault();
    const trimmedVal = e?.target?.value ? e?.target?.value.trim() : '';
    if (!validateValue(trimmedVal)) return;

    switch (timeType) {
      case TIME_TYPE.HH: {
        setHH(formatTimePart(trimmedVal));
        const isValid = isValidTimePart(trimmedVal, TIME_TYPE.HH, false);
        if (isValid) {
          setHHErrorPresent(false);
          setTimeInputError(false);
          !hhErrorPresent && !mmErrorPresent && setError('');
        }
        break;
      }
      case TIME_TYPE.MM: {
        setMM(formatTimePart(trimmedVal));
        const isValid = isValidTimePart(trimmedVal, TIME_TYPE.MM, false);
        if (isValid) {
          setMMErrorPresent(false);
          setTimeInputError(false);
          !hhErrorPresent && !mmErrorPresent && setError('');
        }
        break;
      }
    }
  }

  function resetInputs() {
    setHH(DEFAULT_TIME);
    setMM(DEFAULT_TIME);
    setHHErrorPresent(false);
    setMMErrorPresent(false);
    setTimeInputError(false);
    setError('');
    setDate(DEFAULT_DATE_VALUE);
    setDateError('');
    setTimeValue(moment.invalid());
  }

  function clearAllTimeInputs(): void {
    if (isDisabled) return;
    resetInputs();
  }

  function checkChangeOccured(hh: string, mm: string) {
    if ((hh == DEFAULT_TIME || !hh.length) && (mm == DEFAULT_TIME || !mm.length)) {
      resetInputs();
      return false;
    }
    return true;
  }

  function isValidTimePart(part: string, timePart: string, autoSetErrors = true): boolean {
    function setErrorPresentInTimePart(timeTypeHHMM: string, value: boolean): void {
      if (timeTypeHHMM == TIME_TYPE.HH) {
        setHHErrorPresent(value);
      }
      if (timeTypeHHMM == TIME_TYPE.MM) {
        setMMErrorPresent(value);
      }
    }

    if (!part.length || part.includes('-')) {
      if (autoSetErrors) {
        setError(INCOMPLETE_ERROR);
        setErrorPresentInTimePart(timePart, true);
        setTimeInputError(true);
      }
      return false;
    }
    if (timePart == TIME_TYPE.HH && Number(part) > 23) {
      if (autoSetErrors) {
        setError(INVALID_FORMAT_ERROR);
        setErrorPresentInTimePart(timePart, true);
        setTimeInputError(true);
      }
      return false;
    }
    if (timePart == TIME_TYPE.MM && Number(part) > 59) {
      if (autoSetErrors) {
        setError(INVALID_FORMAT_ERROR);
        setErrorPresentInTimePart(timePart, true);
        setTimeInputError(true);
      }
      return false;
    }

    setErrorPresentInTimePart(timePart, false);
    setTimeInputError(false);
    return true;
  }

  function handleOnblur(e: any, timeType: string): void {
    e.preventDefault();
    let val = e.target.value;
    if (!val.length) {
      val = DEFAULT_TIME;
      if (timeType == TIME_TYPE.HH) {
        setHH(val);
      }
      if (timeType == TIME_TYPE.MM) {
        setMM(val);
      }
    }

    if (e.target.value.length == 1 && e.target.value != '-') {
      val = '0' + e.target.value;
      if (timeType == TIME_TYPE.HH) {
        setHH(val);
      }
      if (timeType == TIME_TYPE.MM) {
        setMM(val);
      }
    }

    if (!checkChangeOccured(hh, mm) || !shouldRunValidation) {
      return;
    }

    const partToRevalidate = timeType == TIME_TYPE.HH ? TIME_TYPE.MM : TIME_TYPE.HH;
    const valueToRevalidate = timeType == TIME_TYPE.HH ? mm : hh;
    const isvalidInputTimeOther = isValidTimePart(valueToRevalidate, partToRevalidate);
    const isvalidInputTimeCurrent = isValidTimePart(e.target.value, timeType);

    if (isvalidInputTimeCurrent && isvalidInputTimeOther) {
      setError('');
      setTimeInputError(false);

      const fmtTime = `${hh}:${mm}`;
      convertAndSetDateValue(fmtTime);
      return;
    } else {
      setTimeInputError(true);
    }
    return;
  }

  function validateValue(val: string): boolean {
    if (!val.includes('-') && val != '' && isNaN(Number(val))) {
      return false;
    }
    return true;
  }

  function convertDateToHHMMFormat(dateISO: string, timeZone: string): any {
    if (!dateISO) {
      return '';
    }

    const tz = moment.tz.zone(timeZone);
    const offset =
      tz && tz.utcOffset(new Date(dateISO).getTime()) - new Date(dateISO).getTimezoneOffset();
    const normalizedDate = moment(dateISO).subtract(offset, 'minutes');
    return {
      time: normalizedDate.format('HH:mm') || '',
      date: normalizedDate.format(DEFAULT_DATE_FORMAT),
    };
  }

  function setHHMMPartOfDate(date: Date | string, timePart: string): moment.Moment | null {
    if (!date || !timePart.length) {
      return null;
    }
    const [hh, mm] = timePart?.split(':');
    return moment(date)
      .hour(Number(hh))
      .minute(Number(mm));
  }

  function shouldShowClearIcon(): string {
    const errorPresent = error.length > 0;
    const className = 'show-icon';
    if (isDisabled) {
      return '';
    }

    if (((hh.length || mm.length) && (hh != DEFAULT_TIME || mm != DEFAULT_TIME)) || errorPresent) {
      return className;
    }

    return '';
  }

  function handleOnDateChange(dateOption: DateOption): void {
    if (!dateOption.value) {
      setDate(dateOption.value);
    } else {
      const temp = moment.tz(`${dateOption.value} ${hh}:${mm}`, timezoneText);
      const finalTime = temp.toISOString();
      setDate(dateOption.value);
      setTimeValue(moment(finalTime));
    }
  }

  const timeInput = (
    <div className={`time-input-ctn ${isMobile ? 'mobile-view' : ''}`}>
      <p className={`input-label ${isRequired ? 'is-required' : ''}`}>{label}</p>
      <div className={`time-input-timezone-ctn ${isMobile ? 'mobile-view' : ''}`}>
        <div className={`time-input-icon-ctn ${isMobile ? 'mobile-view' : ''}`}>
          <div className="outside-click-alerter" ref={ref}>
            <input
              className={`time-input ${hhErrorPresent ? 'error-input' : ''}`}
              type="text"
              maxLength={2}
              onBlur={e => {
                handleOnblur(e, TIME_TYPE.HH);
              }}
              onChange={e => {
                handleOnChange(e, TIME_TYPE.HH);
              }}
              onClick={e => handleHighlightContent(e)}
              disabled={isDisabled}
              value={hh}
            />
            <span>:</span>
            <input
              className={`time-input ${mmErrorPresent ? 'error-input' : ''}`}
              type="text"
              maxLength={2}
              onBlur={e => {
                handleOnblur(e, TIME_TYPE.MM);
              }}
              onChange={e => {
                handleOnChange(e, TIME_TYPE.MM);
              }}
              onClick={e => handleHighlightContent(e)}
              disabled={isDisabled}
              value={mm}
            />
            {is36HoursMode && (
              <SimpleSelect
                selectedOption={{
                  value: date,
                  label: date || DEFAULT_DATE_LABEL,
                }}
                options={[
                  {
                    value: DEFAULT_DATE_VALUE,
                    label: DEFAULT_DATE_LABEL,
                  },
                  {
                    value: startDateLog,
                    label: startDateLog,
                  },
                  {
                    value: endDateLog,
                    label: endDateLog,
                  },
                ]}
                handleChange={handleOnDateChange}
                isDisabled={isDisabled}
              />
            )}
          </div>
          <div className={`clear-icon-ctn ${shouldShowClearIcon()}`}>
            <img
              className="clear-icon-fmt"
              src={ClearIcon}
              alt="clear time input"
              onClick={clearAllTimeInputs}
            />
          </div>
        </div>
        <div
          className={`${
            !isMobile ? 'hos-edit-log_timezone-label' : 'hos-edit-log_mobile-timezone-label'
          }`}
        >
          {`(${timezoneText})`}
        </div>
      </div>
      <p className={`time-format-error ${error?.length > 0 ? 'show-element' : ''}`}>{error}</p>
      <p className={`time-format-error ${dateError?.length > 0 ? 'show-element' : ''}`}>
        {dateError}
      </p>
    </div>
  );

  return timezoneText ? timeInput : null;
};

export default TimeInput;
